import { EventI, EventProfile, EventRole } from "../../../../pojo/EventI";
import { ConflictType } from "../../import.service";
import { Event } from "../../../../pojo/Event";
import Occurrence = Event.Occurrence;
import { S25Util } from "../../../../util/s25-util";
import { EventFormOccurrenceUtil } from "../../../s25-event-creation-form/occurrences/event.form.occurrence.util";
import { ProfileUtil } from "../../../s25-datepattern/profile.util";
import { Contact } from "../../../../pojo/Contact";

export class EventImportUtil {
    public static getConflictTypeName(conflictType: ConflictType, spaces: boolean): string {
        let prefix = spaces ? "Space " : "Resource ";
        switch (conflictType) {
            case "rsrv":
                return prefix + " Conflict";
            case "pending":
                return prefix + " Pending Conflict";
            case "blackout":
                return prefix + " Blackout Conflict";
            case "hours":
                return prefix + " Hours Conflict";
            case "stock":
                return prefix + " Stock Conflict";
            default:
                return prefix + " Unknown Conflict";
        }
    }

    public static addRsrvFromOcc(
        occ: Occurrence,
        spaceIds: number[],
        resourceIdsAndQuantity: string[],
        sharedSpaceIds: Set<number>,
        profile: EventProfile,
    ) {
        let startDt = EventFormOccurrenceUtil.getStartDt(occ);
        let endDt = EventFormOccurrenceUtil.getEndDt(occ);
        let preEventDate = EventFormOccurrenceUtil.getPreDt(occ);
        let postEventDate = EventFormOccurrenceUtil.getPostDt(occ);
        profile.reservation.push({
            reservation_state: 1, // active
            reservation_start_dt: S25Util.date.toS25ISODateTimeStr(startDt),
            reservation_end_dt: S25Util.date.toS25ISODateTimeStr(endDt),
            event_start_dt: S25Util.date.toS25ISODateTimeStr(occ.evStartDt),
            event_end_dt: S25Util.date.toS25ISODateTimeStr(occ.evEndDt),
            pre_event_dt: S25Util.date.toS25ISODateTimeStr(preEventDate),
            post_event_dt: S25Util.date.toS25ISODateTimeStr(postEventDate),
            space_reservation: spaceIds.map((spaceId) => {
                return {
                    space_id: spaceId,
                    share: sharedSpaceIds.has(spaceId) ? "T" : "F",
                    rating: 100,
                };
            }),
            resource_reservation: resourceIdsAndQuantity.map((resourceIdAndQuantity) => {
                return {
                    resource_id: S25Util.toInt(resourceIdAndQuantity.split(":")[0]),
                    quantity: S25Util.toInt(resourceIdAndQuantity.split(":")[1] || "1"),
                };
            }),
        });
    }

    public static addOccurrences(events: EventI[], errorSet: Set<string>) {
        events.forEach((e) => {
            e.profile.forEach((p) => {
                let baseRsrv = p.reservation[0];
                p.reservation = [];
                let spaceIds = S25Util.array.unique(baseRsrv.space_reservation.map((sp) => sp.space_id));
                let resourceIdsAndQuantity = S25Util.array.unique(
                    baseRsrv.resource_reservation.map((rr) => {
                        // to be safe, get the max quantity specified in this reservation in case there is bad data and duplicates
                        let maxQuantity = Math.max(
                            1,
                            rr.quantity || 1,
                            ...baseRsrv.resource_reservation
                                .filter((rr2) => rr2.resource_id === rr.resource_id)
                                .map((rr2) => rr2.quantity || 1),
                        );
                        return rr.resource_id + ":" + maxQuantity;
                    }),
                );
                let sharedSpaceIds: Set<number> = new Set<number>();
                baseRsrv.space_reservation.forEach((sp) => {
                    if (sp.share === "T") {
                        sharedSpaceIds.add(sp.space_id);
                    }
                });
                let profileCode = p.profile_code;
                let adHocDates = S25Util.array.unique(p.ad_hoc_datelist.map((d) => d.ad_hoc_start_dt));
                let firstOcc = p.init_start_dt;
                let firstOccDt = S25Util.date.parse(firstOcc);
                if (!firstOccDt || !S25Util.date.isDate(firstOccDt) || !S25Util.date.isValid(firstOccDt)) {
                    errorSet.add("Invalid first occurrence date: [" + firstOcc + "]");
                    return;
                }
                firstOcc = S25Util.date.toS25ISODateStr(firstOccDt);
                let firstOccDtTm = S25Util.date.parse(firstOcc + "T" + e.start_date);
                let firstOccEndDtTm = S25Util.date.parse(firstOcc + "T" + e.end_date);
                p.init_start_dt = S25Util.date.toS25ISODateTimeStr(firstOccDtTm);
                p.init_end_dt = S25Util.date.toS25ISODateTimeStr(firstOccEndDtTm);
                if (profileCode && firstOcc) {
                    let occs = ProfileUtil.getOccurrences(
                        {
                            evStartDt: firstOccDtTm,
                            evEndDt: firstOccEndDtTm,
                            minutes: {
                                setupMinutes: 0,
                                preMinutes: parseInt(p.pre_event) || 0,
                                postMinutes: parseInt(p.post_event) || 0,
                                takedownMinutes: 0,
                            },
                        },
                        ProfileUtil.getProfileModel(
                            null, // will get parsed from profileCode
                            profileCode,
                            firstOccDt,
                            firstOccDt,
                        ),
                    );
                    p.prof_use = 1;
                    p.rec_type_id = 1;
                    occs.forEach((occ) =>
                        this.addRsrvFromOcc(occ, spaceIds, resourceIdsAndQuantity, sharedSpaceIds, p),
                    );
                } else {
                    if (firstOcc && !adHocDates.find((d) => d === firstOcc)) {
                        adHocDates.unshift(firstOcc);
                    }
                    p.prof_use = 1;
                    p.rec_type_id = 2;
                    p.profile_code = null;
                    let minutes = {
                        setupMinutes: 0,
                        preMinutes: parseInt(p.pre_event) || 0,
                        postMinutes: parseInt(p.post_event) || 0,
                        takedownMinutes: 0,
                    };
                    adHocDates
                        .map((d) => {
                            let occ: Occurrence = {
                                evStartDt: S25Util.date.parse(d + "T" + e.start_date),
                                evEndDt: S25Util.date.parse(d + "T" + e.end_date),
                                minutes: minutes,
                            };
                            return occ;
                        })
                        .forEach((occ) =>
                            this.addRsrvFromOcc(occ, spaceIds, resourceIdsAndQuantity, sharedSpaceIds, p),
                        );
                }
            });
            e.start_date = S25Util.date.toS25ISODateTimeStr(
                e.profile.reduce((min, p) => {
                    let minRsrvDate = p.reservation.reduce((min, r) => {
                        return min <= S25Util.date.parse(r.reservation_start_dt)
                            ? min
                            : S25Util.date.parse(r.reservation_start_dt);
                    }, new Date("2100-12-31T00:00:00"));
                    if (!minRsrvDate) {
                        return min;
                    }
                    return min <= minRsrvDate ? min : minRsrvDate;
                }, new Date("2100-12-31T00:00:00")),
            );
            e.end_date = S25Util.date.toS25ISODateTimeStr(
                e.profile.reduce((max, p) => {
                    let maxRsrvDate = p.reservation.reduce((max, r) => {
                        return max >= S25Util.date.parse(r.reservation_end_dt)
                            ? max
                            : S25Util.date.parse(r.reservation_end_dt);
                    }, new Date("1970-01-01T00:00:00"));
                    if (!maxRsrvDate) {
                        return max;
                    }
                    return max >= maxRsrvDate ? max : maxRsrvDate;
                }, new Date("1970-01-01T00:00:00")),
            );
        });
    }

    public static bindProfiles(events: EventI[]) {
        let profileMap = new Map<string, EventProfile>();
        events.forEach((e) => {
            e.profile = e.profile || [];
            e.profile.forEach((p) => {
                profileMap.set(p.ext_uid, p);
            });
        });
        events.forEach((e) => {
            e.profile.forEach((p) => {
                this.bindProfile(p, profileMap, new Set<number>(), new Set<number>(), new Set<string>(), 0);
            });
        });
    }

    public static bindProfile(
        profile: EventProfile,
        profileMap: Map<string, EventProfile>,
        spaceIds: Set<number>,
        sharedSpaceIds: Set<number>,
        visited: Set<string>,
        depth: number,
    ) {
        if (visited.has(profile.ext_uid)) {
            return;
        }
        visited.add(profile.ext_uid);

        // add all space ids from this profile
        profile.reservation.forEach((r) => {
            r.space_reservation.forEach((sp) => {
                spaceIds.add(sp.space_id);
                sp.share === "T" && sharedSpaceIds.add(sp.space_id);
            });
        });

        // add all space ids from bound profiles recursively
        profile.binding_reservation.forEach((br) => {
            let boundProfile = profileMap.get(br.bound_reservation_ext_uid);
            boundProfile && this.bindProfile(boundProfile, profileMap, spaceIds, sharedSpaceIds, visited, depth + 1);
        });

        if (depth === 0) {
            Array.from(visited).forEach((profileExtUid) => {
                let p = profileMap.get(profileExtUid);
                if (p) {
                    p.binding_reservation = Array.from(visited).map((extUid) => {
                        return { bound_reservation_ext_uid: extUid };
                    });
                    p.reservation.forEach((r) => {
                        r.space_reservation = Array.from(spaceIds).map((spaceId) => {
                            return {
                                space_id: spaceId,
                                share: sharedSpaceIds.has(spaceId) ? "T" : "F",
                            };
                        });
                    });
                }
            });
        }
    }

    public static relateEvents(events: EventI[]) {
        let eventMap = new Map<string, EventI>();
        events.forEach((e) => {
            eventMap.set(e.alien_uid, e);
        });
        events.forEach((e) => {
            this.relateEvent(e, eventMap, new Set<string>(), 0);
        });
    }

    public static relateEvent(event: EventI, eventMap: Map<string, EventI>, visited: Set<string>, depth: number) {
        if (visited.has(event.alien_uid)) {
            return;
        }
        visited.add(event.alien_uid);
        event.relatedEvents.forEach((re) => {
            let relatedEvent = eventMap.get(re.alien_uid);
            if (relatedEvent) {
                this.relateEvent(relatedEvent, eventMap, visited, depth + 1);
            }
        });

        if (depth === 0) {
            // the whole set of related events is now in visited
            // set relatedEvents for each event to the visited set minus itself
            Array.from(visited).forEach((eventExtUid) => {
                let e = eventMap.get(eventExtUid);
                if (e) {
                    e.relatedEvents = Array.from(visited).map((extUid) => {
                        return { alien_uid: extUid };
                    });
                }
            });
        }
    }

    public static validateEvents(events: EventI[]): string[] {
        let errors: string[] = [];
        events.forEach((e) => {
            if (!e.alien_uid) {
                errors.push("Event is missing alien_uid");
            }
            if (!e.event_name) {
                errors.push(`Event [${e.alien_uid}] is missing event name`);
            }
            if (events.filter((ev) => ev.alien_uid === e.alien_uid).length > 1) {
                errors.push(`Event [${e.alien_uid}] has duplicate alien_uid`);
            }
            if (!e.state) {
                errors.push(`Event [${e.alien_uid}] is missing event state`);
            }
            if (
                !e.role?.find(
                    (er: EventRole) => er?.role_id === Contact.Role.Event.id.Requestor && er.contact?.contact_id,
                )
            ) {
                errors.push(`Event [${e.alien_uid}] is missing requestor role`);
            }
            if (
                !e.role?.find(
                    (er: EventRole) => er?.role_id === Contact.Role.Event.id.Scheduler && er.contact?.contact_id,
                )
            ) {
                errors.push(`Event [${e.alien_uid}] is missing schedule role`);
            }
            if (!e.start_date || !S25Util.date.isValid(S25Util.date.parse(e.start_date))) {
                errors.push(`Event [${e.alien_uid}] has invalid start date: [${e.start_date}]`);
            }
            if (!e.end_date || !S25Util.date.isValid(S25Util.date.parse(e.end_date))) {
                errors.push(`Event [${e.alien_uid}] has invalid end date: [${e.end_date}]`);
            }
            if (e.start_date && e.end_date && S25Util.date.parse(e.start_date) >= S25Util.date.parse(e.end_date)) {
                errors.push(`Event [${e.alien_uid}] has start date after (or equal to) end date`);
            }
            if (e.profile.length === 0) {
                errors.push(`Event [${e.alien_uid}] has no profiles defined`);
            }
            e.profile.forEach((p) => {
                if (!p.ext_uid) {
                    errors.push(`Event [${e.alien_uid}] profile is missing ext_uid`);
                }
                if (e.profile.filter((profile) => profile.ext_uid === p.ext_uid).length > 1) {
                    errors.push(`Event [${e.alien_uid}] has duplicate profile ext_uid: [${p.ext_uid}]`);
                }
                if (!p.init_start_dt || !S25Util.date.isValid(S25Util.date.parse(p.init_start_dt))) {
                    errors.push(
                        `Event [${e.alien_uid}] profile [${p.ext_uid}] has invalid init start date: [${p.init_start_dt}]`,
                    );
                }
                if (!p.init_end_dt || !S25Util.date.isValid(S25Util.date.parse(p.init_end_dt))) {
                    errors.push(
                        `Event [${e.alien_uid}] profile [${p.ext_uid}] has invalid init end date: [${p.init_end_dt}]`,
                    );
                }
                if (
                    p.init_start_dt &&
                    p.init_end_dt &&
                    S25Util.date.parse(p.init_start_dt) >= S25Util.date.parse(p.init_end_dt)
                ) {
                    errors.push(
                        `Event [${e.alien_uid}] profile [${p.ext_uid}] has init start date after (or equal to) init end date`,
                    );
                }
                if (p.profile_code && p.init_start_dt && S25Util.date.isValid(S25Util.date.parse(p.init_start_dt))) {
                    let firstOccDt = S25Util.date.parse(p.init_start_dt);
                    let profileModel = ProfileUtil.getProfileModel(
                        null, // will get parsed from profileCode
                        p.profile_code,
                        firstOccDt,
                        firstOccDt,
                    );
                    if (!profileModel?.type) {
                        errors.push(
                            `Event [${e.alien_uid}] profile [${p.ext_uid}] has invalid profile code: [${p.profile_code}]`,
                        );
                    }
                }
                if (p.reservation.length === 0) {
                    errors.push(`Event [${e.alien_uid}] profile [${p.ext_uid}] has no reservations defined`);
                }
                p.reservation.forEach((r) => {
                    if (!r.reservation_start_dt || !S25Util.date.isValid(S25Util.date.parse(r.reservation_start_dt))) {
                        errors.push(
                            `Event [${e.alien_uid}] profile [${p.ext_uid}] reservation has invalid start date: [${r.reservation_start_dt}]`,
                        );
                    }
                    if (!r.reservation_end_dt || !S25Util.date.isValid(S25Util.date.parse(r.reservation_end_dt))) {
                        errors.push(
                            `Event [${e.alien_uid}] profile [${p.ext_uid}] reservation has invalid end date: [${r.reservation_end_dt}]`,
                        );
                    }
                    if (S25Util.date.parse(r.reservation_start_dt) >= S25Util.date.parse(r.reservation_end_dt)) {
                        errors.push(
                            `Event [${e.alien_uid}] profile [${p.ext_uid}] reservation has start date after (or equal to) end date`,
                        );
                    }
                });
                let uniqueReservations = new Set<string>();
                p.reservation.forEach((r) => {
                    let key = `${p.ext_uid}-${r.reservation_start_dt}-${r.reservation_end_dt}`;
                    if (uniqueReservations.has(key)) {
                        errors.push(
                            `Event [${e.alien_uid}] profile [${p.ext_uid}] has duplicate reservation: [${key}]`,
                        );
                    } else {
                        uniqueReservations.add(key);
                    }
                });
            });
        });
        return errors;
    }
}
